import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { CountryRegionPicker } from '@/components/business/business-locations/country-region-picker';
import { LocationMapPicker } from '@/components/business/business-locations/location-map-picker';
import { businessLocationsQueryOptions, locationsByIdsQueryOptions } from '@/lib/query-options';
import type { ColumnDef } from '@tanstack/react-table';
import type { Location } from '@/lib/types';
import { usePostHog } from 'posthog-js/react';

interface CampaignTargetViewProps {
  locations: Location[];
  setLocations: (locations: Location[]) => void;
  disabled?: boolean;
  /** Business id for the map picker's boundary overlays + business-location
   * candidates. Falls back to the list-based picker when absent. */
  businessId?: number;
  /** Campaign name, shown in the Add-location dialog title. */
  campaignName?: string;
  /** #5466: criteria ids already excluded on the campaign — the picker badges them so the
   * user sees that targeting one would conflict with an existing exclusion. */
  excludedCriteriaIds?: Set<number>;
}

const formatTargetType = (targetType: string): string => {
  return targetType.charAt(0).toUpperCase() + targetType.slice(1).toLowerCase();
};

const getTargetTypePillStyle = (targetType: string) => {
  const type = targetType.toLowerCase();
  switch (type) {
    case 'country':
      return 'bg-purple-50 text-purple-600';
    case 'region':
    case 'state':
      return 'bg-blue-50 text-blue-600';
    case 'city':
      return 'bg-green-50 text-green-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const formatReach = (reach: number | undefined | null) => {
  if (reach == null || isNaN(reach)) return '—';
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(reach);
};

const formatCanonicalName = (name: string): string => {
  return name.replace(/,/g, ', ');
};

export const CampaignTargetView: React.FC<CampaignTargetViewProps> = ({
  locations,
  setLocations,
  disabled = false,
  businessId,
  campaignName,
  excludedCriteriaIds,
}) => {
  const posthog = usePostHog();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState<Location | null>(null);

  // Business's saved catalog locations — the checklist candidates in the Add dialog
  // (alongside the campaign's already-targeted locations). Resolved name-side via
  // /locations/by-ids, the same way the business "Add location" dialog does.
  const { data: businessLocations } = useQuery({
    ...businessLocationsQueryOptions(businessId ?? 0),
    enabled: businessId != null && isModalOpen,
  });
  const businessCatalogIds = useMemo(
    () =>
      (businessLocations ?? [])
        .filter((l) => !l.dismissed_by_user && (l.variant === 'country' || l.variant === 'admin') && l.location_id != null)
        .map((l) => l.location_id as number),
    [businessLocations],
  );
  const { data: businessCandidates } = useQuery({
    ...locationsByIdsQueryOptions(businessCatalogIds),
    enabled: isModalOpen && businessCatalogIds.length > 0,
  });
  const [modalSelected, setModalSelected] = useState<Location[]>([]);

  const columns: ColumnDef<Location>[] = [
    {
      accessorKey: 'canonical_name',
      header: 'Location',
      // Pin to half-width so the Type column lands at the same x as the Excluded
      // Locations table below it (which has no Reach column). #4642 alignment.
      meta: { className: 'w-1/2 px-6' },
      cell: ({ row }) => (
        <div className='flex items-center gap-3'>
          <MapPin className='h-4 w-4 text-gray-500' />
          <span className='text-sm font-medium text-gray-900'>{formatCanonicalName(row.original.canonical_name)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'target_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetTypePillStyle(row.original.target_type)}`}>
          {formatTargetType(row.original.target_type)}
        </span>
      ),
    },
    {
      accessorKey: 'reach',
      header: 'Reach',
      cell: ({ row }) => (
        <span className='text-sm text-gray-900'>{formatReach(row.original.reach)}</span>
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      meta: { className: 'w-12 px-3 py-3' },
      cell: ({ row }) => (
        !disabled ? (
          <IconButton
            variant='destructive'
            aria-label='Remove location'
            onClick={() => setDeletingLocation(row.original)}
          >
            <Trash2 className='h-4 w-4' />
          </IconButton>
        ) : null
      ),
    },
  ];

  // The dialog is pre-populated with the campaign's current locations, so the
  // checked set IS the desired target list — apply it as-is (not a merge).
  const handleAddSelected = () => {
    setLocations(modalSelected);
    posthog?.capture('campaign_targets_updated', { location_count: modalSelected.length });
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!deletingLocation) return;
    const newLocations = locations.filter((loc) => loc.canonical_name !== deletingLocation.canonical_name);
    setLocations(newLocations);
    posthog?.capture('campaign_targets_updated', { location_count: newLocations.length });
    setDeletingLocation(null);
  };

  const openAddModal = () => {
    // Pre-populate with the campaign's current locations (checked); business
    // locations come in as additional checklist candidates.
    setModalSelected([...locations]);
    setIsModalOpen(true);
  };

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>Locations</h3>
          <p className='text-sm text-gray-700 mt-1'>Target specific geographic areas for your ads.</p>
        </div>
        {!disabled && (
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={openAddModal}
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Locations
          </Button>
        )}
      </div>

      {/* Table */}
      <DataTable
        data={locations}
        columns={columns}
        variant='bordered'
        pageSize={999}
        initialSorting={[{ id: 'canonical_name', desc: false }]}
        emptyIcon={MapPin}
        emptyTitle='No Locations'
        emptyDescription='Add locations to target specific geographic areas for your ads.'
      />

      {/*
        Add Location modal: the map picker (catalog-only) when we have a businessId,
        else the list-based CountryRegionPicker fallback. A campaign's
        target_locations are geo `Location` rows keyed by criteria_id, so the Radius
        / Worldwide tabs (which only apply to BusinessLocation variants) are hidden.
      */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          className='flex h-[85vh] max-h-[85vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1100px]'
          aria-describedby={undefined}
        >
          <DialogHeader className='border-b border-gray-200 px-5 py-4'>
            <DialogTitle className='truncate'>
              Add location{campaignName ? ` to ${campaignName}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className='min-h-0 flex-1 overflow-hidden'>
            {businessId != null ? (
              <LocationMapPicker
                businessId={businessId}
                catalogOnly
                candidates={businessCandidates ?? []}
                selected={modalSelected}
                onChange={setModalSelected}
                excludedCriteriaIds={excludedCriteriaIds}
              />
            ) : (
              <div className='h-full overflow-y-auto p-5'>
                <CountryRegionPicker selected={modalSelected} onChange={setModalSelected} />
              </div>
            )}
          </div>

          <div className='flex justify-end gap-2 border-t border-gray-200 px-5 py-3'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              className='bg-[#290BD9] text-white hover:bg-[#2309C5]'
              onClick={handleAddSelected}
              disabled={modalSelected.length === 0}
            >
              {`Save ${modalSelected.length} location${modalSelected.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingLocation}
        onClose={() => setDeletingLocation(null)}
        onConfirm={handleDeleteConfirm}
        title="Remove Location"
        description={`Are you sure you want to remove "${deletingLocation ? formatCanonicalName(deletingLocation.canonical_name) : ''}" from targeting? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </div>
  );
};
